import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { faBookOpen, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useCompassCard } from '@scale/compass-ui/store';
import { Code, RadioGroup, Text, Heading, Callout, Icon } from '@scale/scaleui-radix';
import { getTaskIdColumn, getSampleTaskId, getMetadataColumns, getSampleValue } from './utils';
import { Column } from '@scale/compass-shared/types';
import {
  AudioTranscriptionModel,
  type TranscribeAudioContext,
} from '@scale/genai-compass-shared/cards/transcribe_audio';

import { CompassInstructions } from '@scale/compass-ui/components/CardInstructions';
import { ColumnPreview } from './ColumnPreview';

const TranscribeAudioCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardContext } = useCompassCard<TranscribeAudioContext>(index);
  const context = card.defaultContext;
  const { retrievedOutput } = useCompassCard(index - 1);

  const [selectedColumns, setSelectedColumns] = [
    context.columns,
    (selectedColumns: Column[]) => {
      const newSelectedColumns = metadataColumns.filter((column: Column) =>
        selectedColumns.some(selected => selected.header === column.header),
      );
      updateCardContext({ ...context, columns: newSelectedColumns });
    },
  ];

  const selectedModel = context.model ?? AudioTranscriptionModel.AWS;
  const enableSpeakerDiarization = context.enableSpeakerDiarization ?? false;

  const handleModelChange = (value: string) => {
    updateCardContext({
      ...context,
      model: value as AudioTranscriptionModel,
    });
  };

  const handleDiarizationChange = (value: string) => {
    updateCardContext({
      ...context,
      enableSpeakerDiarization: value === 'true',
    });
  };

  const taskIdColumn = getTaskIdColumn(retrievedOutput);
  const sampleTaskId = getSampleTaskId(retrievedOutput, taskIdColumn);
  const metadataColumns = getMetadataColumns(retrievedOutput);

  const tableData = metadataColumns.map((col: Column) => ({
    header: col.header,
    value:
      getSampleValue(retrievedOutput, col.header) === '<empty>' ? (
        <Code variant="soft" color="neutral">
          {'<empty>'}
        </Code>
      ) : (
        getSampleValue(retrievedOutput, col.header)
      ),
  }));

  return (
    <div className="flex flex-col xl:!flex-row gap-4 lg:gap-8">
      <div className="w-full xl:!w-1/2">
        <CompassInstructions
          header="How to use the Transcribe Audio card"
          storageKey="transcribeAudioInstructionsExpanded"
          disableCollapse={true}
          icon={faBookOpen}
        >
          <Callout.Text>
            <Text as="p">
              <div className="text-sm text-gray-600">
                This card will transcribe audio from URLs in the selected columns. Here's how to use
                it:
                <ul className="list-disc ml-6 mt-2">
                  <li>Choose a transcription model (AWS Transcribe or Whisper)</li>
                  <li>Speaker diarization is only available for AWS Transcribe</li>
                  <li>Select columns containing S3 URLs to audio files</li>
                  <li>
                    A <code>taskId</code> column must exist for identifying tasks
                  </li>
                  <li>
                    Transcripts will be saved in new columns named{' '}
                    <code>{`{columnName}_transcript`}</code>
                  </li>
                </ul>
              </div>
            </Text>
          </Callout.Text>
        </CompassInstructions>

        <div className="flex flex-col gap-4 mt-6">
          <div className="flex flex-col gap-2">
            <Heading size="2" weight="medium" className="text-gray-700">
              Transcription Model
            </Heading>
            <RadioGroup.Root
              value={selectedModel}
              onValueChange={handleModelChange}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroup.Item value={AudioTranscriptionModel.AWS} id="aws" />
                <label htmlFor="aws" className="text-sm">
                  <Text weight="medium">AWS Transcribe</Text>
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroup.Item value={AudioTranscriptionModel.WHISPER} id="whisper" />
                <label htmlFor="whisper" className="text-sm">
                  <Text weight="medium">Whisper</Text>
                </label>
              </div>
            </RadioGroup.Root>
          </div>

          {selectedModel === AudioTranscriptionModel.AWS && (
            <div className="flex flex-col gap-2">
              <Heading size="2" weight="medium" className="text-gray-700">
                Speaker Diarization
              </Heading>
              <RadioGroup.Root
                value={enableSpeakerDiarization.toString()}
                onValueChange={handleDiarizationChange}
                className="flex flex-col gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroup.Item value="false" id="no-diarization" />
                  <label htmlFor="no-diarization" className="text-sm">
                    <Text weight="medium">Disabled</Text> - Raw transcript only
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroup.Item value="true" id="enable-diarization" />
                  <label htmlFor="enable-diarization" className="text-sm">
                    <Text weight="medium">Enabled</Text> - Label user and assistant speakers +
                    timestamps
                  </label>
                </div>
              </RadioGroup.Root>
            </div>
          )}
        </div>
      </div>

      <div className="w-full xl:!w-1/2">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Heading size="2" weight="medium" className="text-gray-700">
              Task ID Column
            </Heading>
            <div className="flex items-center gap-2">
              <Icon icon={faCircleInfo} className="text-gray-500" size="sm" label="Info" />
              <Text as="p" size="2" className="text-gray-600">
                Column <Code>{taskIdColumn}</Code> is used to identify tasks
              </Text>
            </div>
          </div>

          <ColumnPreview
            sampleTaskId={sampleTaskId}
            tableData={tableData}
            isLoading={!retrievedOutput}
            metadataColumns={metadataColumns}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
          />
        </div>
      </div>
    </div>
  );
};

TranscribeAudioCardComponent.displayName = 'TranscribeAudioCardComponent';

export default TranscribeAudioCardComponent;
